/**
 * Núcleo PURO da importação de produtos via planilha (docs/screens/
 * product-import.md). Zero DOM, zero exceljs — a leitura do arquivo vive em
 * `productSheet.ts` e a escrita na callable `importProducts`. Assim o grosso
 * da lógica (matching de labels traduzidos, validação por linha, dedupe) roda
 * em teste de unidade sem jsdom.
 *
 * Origem: sem issue — nasceu em `apps/app/src/features/products/import/
 * importSpreadsheet.ts`; veio para `packages/types` na #776 (2026-09-29
 * 13:16 +0200) para o CRM usar a MESMA revisão do app web ("importar
 * planilha como já tem no app web", owner).
 *
 * Usado por: `apps/app` `ProductImportPage`; CRM `ProductImportPage`;
 * `productSheet.ts` (critério de linha vazia e de header).
 *
 * Arquitetura: a revisão é do cliente; a palavra final é do servidor
 * (`importProducts` revalida, deduplica contra o catálogo atual e aplica o
 * teto). `reconcilePlanAgainstCatalog` continua aqui para o cliente mostrar o
 * mesmo resultado antes de enviar.
 *
 * Histórico:
 *  - 2026-09-29 13:11 +0200 — (#776) movido do apps/app; imports relativos;
 *    `ProductFormValues` → `ProductFields` (`productCatalog.ts`).
 */
import {
  ALL_ALLERGENS,
  ALL_PRODUCT_CATEGORIES,
  ALL_UNITS_OF_MEASURE,
  type Allergen,
  type ProductCategory,
  type UnitOfMeasure,
} from './enums';
import {
  CreateProductInput,
  MAX_PRODUCTS_PER_TENANT,
  MAX_VALIDITY_DURATION_HOURS,
  type ProductValidityRule,
} from './schemas';
import { normalizeForSearch } from './searchNormalize';

/** Limite de linhas de DADOS por arquivo (header fora da conta). */
export const IMPORT_MAX_ROWS = 500;

// Derivado do contrato compartilhado (8760h / 24 = 365) — não re-declarar
// o limite ao lado do exportado pelo @etiquetachef/types.
const MAX_VALIDITY_DAYS = MAX_VALIDITY_DURATION_HOURS / 24;
const MAX_MIN_STOCK = 1_000_000;

/** Valor de célula já normalizado pelo `excelIO` (richText/fórmula → texto). */
export type CellValue = string | number | null;

/**
 * Assinatura mínima de `t` — desacopla do i18next para o parser ser
 * testável com um dicionário simples.
 */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

export type ColumnKey =
  | 'name'
  | 'category'
  | 'unit'
  | 'rule1Name'
  | 'rule1Days'
  | 'rule2Name'
  | 'rule2Days'
  | 'rule3Name'
  | 'rule3Days'
  | 'rule4Name'
  | 'rule4Days'
  | 'rule5Name'
  | 'rule5Days'
  | 'description'
  | 'ingredients'
  | 'allergens'
  | 'minStock';

/** Ordem canônica das 17 colunas do modelo (A..Q). */
export const IMPORT_COLUMNS: readonly ColumnKey[] = [
  'name',
  'category',
  'unit',
  'rule1Name',
  'rule1Days',
  'rule2Name',
  'rule2Days',
  'rule3Name',
  'rule3Days',
  'rule4Name',
  'rule4Days',
  'rule5Name',
  'rule5Days',
  'description',
  'ingredients',
  'allergens',
  'minStock',
];

export const REQUIRED_COLUMNS: readonly ColumnKey[] = [
  'name',
  'category',
  'unit',
  'rule1Name',
  'rule1Days',
];

export type RowErrorCode =
  | 'NAME_EMPTY'
  | 'NAME_TOO_LONG'
  | 'CATEGORY_MISSING'
  | 'CATEGORY_UNKNOWN'
  | 'UNIT_MISSING'
  | 'UNIT_UNKNOWN'
  | 'RULE1_MISSING'
  | 'RULE_NAME_INVALID'
  | 'RULE_DAYS_INVALID'
  | 'RULE_PAIR_INCOMPLETE'
  | 'ALLERGEN_UNKNOWN'
  | 'INGREDIENT_INVALID'
  | 'DESCRIPTION_TOO_LONG'
  | 'MIN_STOCK_INVALID'
  | 'ROW_INVALID';

/** `value` é o texto ofensivo, exibido interpolado na mensagem i18n. */
export type RowError = { code: RowErrorCode; value?: string };

export type RowStatus =
  | 'READY'
  | 'DUPLICATE_CATALOG'
  | 'DUPLICATE_SHEET'
  // Linha válida que não cabe no teto de MAX_PRODUCTS_PER_TENANT — a
  // importação preenche até completar 1.000 e pula o excedente (decisão
  // do owner, 2026-08-11). Demote-only: nunca é promovida de volta no
  // confirm (o usuário aprovou o conjunto mostrado na revisão).
  | 'LIMIT_REACHED'
  | 'ERROR';

/**
 * Payload por linha: os campos do formulário do produto + o minStock da
 * coluna Q. Declarado aqui (e não importado de `productCatalog.ts`) porque
 * aquele módulo importa `IMPORT_MAX_ROWS` deste — evita o ciclo.
 */
export type ImportProductValues = {
  name: string;
  description: string;
  category: ProductCategory;
  unit: UnitOfMeasure;
  sku: string;
  barcode: string;
  ingredients: string[];
  allergens: Allergen[];
  validityRules: ProductValidityRule[];
  supplierIds: string[];
  minStock: number | null;
};

export type ReviewRow = {
  /** Nº da linha no Excel (1-based; header é a linha 1). */
  rowNumber: number;
  name: string;
  status: RowStatus;
  errors: RowError[];
  values: ImportProductValues | null;
};

export type ImportPlan = {
  rows: ReviewRow[];
  counts: { ready: number; duplicates: number; errors: number; limited: number };
};

export type SheetError = 'MISSING_COLUMNS' | 'EMPTY' | 'TOO_MANY_ROWS';

export type ParseSheetResult =
  | { kind: 'OK'; plan: ImportPlan }
  | { kind: 'SHEET_ERROR'; error: SheetError; missingColumns: ColumnKey[] };

export type LabelMaps = {
  category: Map<string, ProductCategory>;
  unit: Map<string, UnitOfMeasure>;
  allergen: Map<string, Allergen>;
  headers: Map<string, ColumnKey>;
};

/** Cabeçalho traduzido de uma coluna do modelo ("Validade 2 – Dias"). */
export function columnHeaderLabel(t: Translate, key: ColumnKey): string {
  const rule = /^rule([1-5])(Name|Days)$/.exec(key);
  if (rule !== null) {
    const i18nKey =
      rule[2] === 'Name'
        ? 'app.products.import.columns.ruleName'
        : 'app.products.import.columns.ruleDays';
    return t(i18nKey, { n: Number(rule[1]) });
  }
  return t(`app.products.import.columns.${key}`);
}

// Sufixo parentético dos labels ("Quilograma (kg)" → "quilograma") — o
// usuário pode digitar sem o parêntese; os dois lados (chave do mapa e
// lookup) removem, cinto e suspensório.
function stripParenSuffix(normalized: string): string {
  return normalized.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

function addLabel<T>(map: Map<string, T>, raw: string, code: T): void {
  const norm = normalizeForSearch(raw);
  // Primeira inserção ganha: código canônico > locale atual > pt-BR.
  if (norm.length > 0 && !map.has(norm)) map.set(norm, code);
  const stripped = stripParenSuffix(norm);
  if (stripped.length > 0 && !map.has(stripped)) map.set(stripped, code);
}

function lookupLabel<T>(map: Map<string, T>, raw: string): T | null {
  const norm = normalizeForSearch(raw);
  const direct = map.get(norm);
  if (direct !== undefined) return direct;
  const stripped = map.get(stripParenSuffix(norm));
  return stripped ?? null;
}

/**
 * Mapas reversos `label normalizado → código do enum` (e header → coluna).
 * Aceita o **idioma atual da UI** (`t`) e o **pt-BR canônico**
 * (`tCanonical` — a planilha pode ter sido gerada noutro computador), além
 * do próprio código do enum ("POULTRY", "KILOGRAM"). Comparação
 * caixa+acento-insensível via `normalizeForSearch`. Não aceita os 10
 * locales simultâneos — colisão entre línguas.
 */
export function buildLabelMaps(t: Translate, tCanonical: Translate): LabelMaps {
  const category = new Map<string, ProductCategory>();
  for (const code of ALL_PRODUCT_CATEGORIES) {
    addLabel(category, code, code);
    addLabel(category, t(`app.products.categoryOptions.${code}`), code);
    addLabel(category, tCanonical(`app.products.categoryOptions.${code}`), code);
  }
  const unit = new Map<string, UnitOfMeasure>();
  for (const code of ALL_UNITS_OF_MEASURE) {
    addLabel(unit, code, code);
    addLabel(unit, t(`app.products.unitOptions.${code}`), code);
    addLabel(unit, tCanonical(`app.products.unitOptions.${code}`), code);
  }
  const allergen = new Map<string, Allergen>();
  for (const code of ALL_ALLERGENS) {
    addLabel(allergen, code, code);
    addLabel(allergen, t(`app.products.allergenOptions.${code}`), code);
    addLabel(allergen, tCanonical(`app.products.allergenOptions.${code}`), code);
  }
  const headers = new Map<string, ColumnKey>();
  for (const key of IMPORT_COLUMNS) {
    addLabel(headers, columnHeaderLabel(t, key), key);
    addLabel(headers, columnHeaderLabel(tCanonical, key), key);
  }
  return { category, unit, allergen, headers };
}

function cellRaw(row: CellValue[], idx: number | undefined): CellValue {
  if (idx === undefined) return null;
  return row[idx] ?? null;
}

// Célula numérica onde se espera texto vale como texto ("123" é nome
// legítimo); trim geral.
function cellText(row: CellValue[], idx: number | undefined): string {
  const v = cellRaw(row, idx);
  if (v === null) return '';
  return String(v).trim();
}

/**
 * Linha sem conteúdo útil (só null/whitespace). Exportado para o
 * `excelIO` usar o MESMO critério ao contar linhas contra o limite de
 * 500 — divergência ali truncaria dados silenciosamente.
 */
export function isRowEmpty(row: CellValue[]): boolean {
  return row.every((v) => v === null || String(v).trim().length === 0);
}

/**
 * Inteiro dentro de [min, max]; fração/texto/fora do range → null.
 *
 * Célula TEXTO exige só dígitos: `Number("1.000")` seria 1 (o JS lê "."
 * como decimal, não como separador de milhar pt-BR) e o import gravaria
 * silenciosamente o valor errado. Célula numérica do Excel chega como
 * `number` e não passa por aqui.
 */
function parseIntInRange(v: CellValue, min: number, max: number): number | null {
  const text = v === null ? '' : String(v).trim();
  if (text.length === 0) return null;
  if (typeof v !== 'number' && !/^\d+$/.test(text)) return null;
  const n = typeof v === 'number' ? v : Number(text);
  if (!Number.isInteger(n) || n < min || n > max) return null;
  return n;
}

function splitList(raw: string): string[] {
  return raw
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// Cinto e suspensório: mesmo com o parser validando campo a campo, a linha
// READY é revalidada com o CONTRATO compartilhado antes de habilitar o
// CTA — drift entre parser e schema vira ROW_INVALID, não escrita
// inválida. Derivado de `CreateProductInput` via `.pick` (não é cópia
// manual: se o contrato apertar um limite, este aperta junto).
const RowSanitySchema = CreateProductInput.pick({
  name: true,
  description: true,
  category: true,
  unit: true,
  ingredients: true,
  validityRules: true,
  minStock: true,
});

type ColumnIndex = ReadonlyMap<ColumnKey, number>;

// Pares (nome, dias) das 5 regras de validade, na ordem do modelo. O par
// 1 é a regra default e é obrigatório.
const RULE_COLUMN_PAIRS: readonly { nameCol: ColumnKey; daysCol: ColumnKey }[] = [
  { nameCol: 'rule1Name', daysCol: 'rule1Days' },
  { nameCol: 'rule2Name', daysCol: 'rule2Days' },
  { nameCol: 'rule3Name', daysCol: 'rule3Days' },
  { nameCol: 'rule4Name', daysCol: 'rule4Days' },
  { nameCol: 'rule5Name', daysCol: 'rule5Days' },
];

function parseRow(
  row: CellValue[],
  rowNumber: number,
  cols: ColumnIndex,
  maps: LabelMaps,
): ReviewRow {
  const errors: RowError[] = [];

  const name = cellText(row, cols.get('name'));
  if (name.length === 0) errors.push({ code: 'NAME_EMPTY' });
  else if (name.length > 120) errors.push({ code: 'NAME_TOO_LONG' });

  // Célula vazia ganha mensagem própria — "Categoria não reconhecida: \"\""
  // confundiria mais do que ajuda.
  const categoryRaw = cellText(row, cols.get('category'));
  const category = categoryRaw.length > 0 ? lookupLabel(maps.category, categoryRaw) : null;
  if (categoryRaw.length === 0) errors.push({ code: 'CATEGORY_MISSING' });
  else if (category === null) errors.push({ code: 'CATEGORY_UNKNOWN', value: categoryRaw });

  const unitRaw = cellText(row, cols.get('unit'));
  const unit = unitRaw.length > 0 ? lookupLabel(maps.unit, unitRaw) : null;
  if (unitRaw.length === 0) errors.push({ code: 'UNIT_MISSING' });
  else if (unit === null) errors.push({ code: 'UNIT_UNKNOWN', value: unitRaw });

  const validityRules: ProductValidityRule[] = [];
  for (const [i, pair] of RULE_COLUMN_PAIRS.entries()) {
    const n = i + 1;
    const ruleName = cellText(row, cols.get(pair.nameCol));
    const daysCell = cellRaw(row, cols.get(pair.daysCol));
    const daysText = daysCell === null ? '' : String(daysCell).trim();
    const hasName = ruleName.length > 0;
    const hasDays = daysText.length > 0;
    if (!hasName && !hasDays) {
      // Par 1 é obrigatório (é a regra default); 2..5 ausentes são ok.
      if (n === 1) errors.push({ code: 'RULE1_MISSING' });
      continue;
    }
    if (!hasName || !hasDays) {
      // Par pela metade invalida a LINHA inteira — não importar produto
      // com regra de validade ambígua.
      if (n === 1) errors.push({ code: 'RULE1_MISSING' });
      else errors.push({ code: 'RULE_PAIR_INCOMPLETE', value: String(n) });
      continue;
    }
    if (ruleName.length > 100) {
      errors.push({ code: 'RULE_NAME_INVALID', value: ruleName });
      continue;
    }
    const days = parseIntInRange(daysCell, 1, MAX_VALIDITY_DAYS);
    if (days === null) {
      errors.push({ code: 'RULE_DAYS_INVALID', value: daysText });
      continue;
    }
    // Persistência é em horas (paridade com o editor: dias × 24); a regra
    // 1 é a default — exatamente uma por produto.
    validityRules.push({ name: ruleName, durationHours: days * 24, isDefault: n === 1 });
  }

  const description = cellText(row, cols.get('description'));
  if (description.length > 1000) errors.push({ code: 'DESCRIPTION_TOO_LONG' });

  const ingredients: string[] = [];
  for (const item of splitList(cellText(row, cols.get('ingredients')))) {
    if (!ingredients.includes(item)) ingredients.push(item);
  }
  if (ingredients.length > 80 || ingredients.some((i) => i.length > 120)) {
    errors.push({ code: 'INGREDIENT_INVALID' });
  }

  const allergens: Allergen[] = [];
  for (const token of splitList(cellText(row, cols.get('allergens')))) {
    const allergen = lookupLabel(maps.allergen, token);
    if (allergen === null) {
      // Token desconhecido invalida a linha inteira: ignorar criaria
      // produto com rotulagem de alérgenos incompleta (risco sanitário).
      errors.push({ code: 'ALLERGEN_UNKNOWN', value: token });
    } else if (!allergens.includes(allergen)) {
      allergens.push(allergen);
    }
  }

  const minStockCell = cellRaw(row, cols.get('minStock'));
  const minStockText = minStockCell === null ? '' : String(minStockCell).trim();
  let minStock: number | null = null;
  if (minStockText.length > 0) {
    minStock = parseIntInRange(minStockCell, 0, MAX_MIN_STOCK);
    if (minStock === null) errors.push({ code: 'MIN_STOCK_INVALID', value: minStockText });
  }

  if (errors.length > 0 || category === null || unit === null) {
    return { rowNumber, name, status: 'ERROR', errors, values: null };
  }

  const values: ImportProductValues = {
    name,
    description,
    category,
    unit,
    sku: '',
    barcode: '',
    ingredients,
    allergens,
    validityRules,
    supplierIds: [],
    minStock,
  };
  const sanity = RowSanitySchema.safeParse(values);
  if (!sanity.success) {
    return { rowNumber, name, status: 'ERROR', errors: [{ code: 'ROW_INVALID' }], values: null };
  }
  return { rowNumber, name, status: 'READY', errors: [], values };
}

/**
 * Marca com `LIMIT_REACHED` as linhas READY que não cabem no teto de
 * produtos do tenant, na ordem da planilha. Demote-only: uma linha
 * `LIMIT_REACHED` NUNCA volta a READY — a revisão mostrou ao usuário
 * exatamente o que seria importado, e promover no confirm gravaria um
 * conjunto diferente do aprovado (drift de consentimento).
 */
function applyCapacity(rows: ReviewRow[], existingCount: number): ReviewRow[] {
  let slots = Math.max(0, MAX_PRODUCTS_PER_TENANT - existingCount);
  return rows.map((row): ReviewRow => {
    if (row.status !== 'READY' || row.values === null) return row;
    if (slots > 0) {
      slots -= 1;
      return row;
    }
    return { ...row, status: 'LIMIT_REACHED' };
  });
}

/**
 * Mapeia o header (linha 1) para a posição de cada coluna do modelo.
 * Header repetido: a primeira coluna ganha. Célula que não casa com
 * nenhum label conhecido é ignorada (coluna extra do usuário).
 */
function resolveHeaderColumns(header: CellValue[], maps: LabelMaps): Map<ColumnKey, number> {
  const cols = new Map<ColumnKey, number>();
  header.forEach((cell, idx) => {
    if (cell === null) return;
    const key = lookupLabel(maps.headers, String(cell));
    if (key !== null && !cols.has(key)) cols.set(key, idx);
  });
  return cols;
}

/**
 * true se a linha tem todas as colunas obrigatórias do modelo — o
 * critério que o `excelIO` usa para ESCOLHER a aba do arquivo (#335).
 * O Numbers, ao exportar para .xlsx, insere uma aba "Resumo da
 * Exportação" na frente; ler sempre a primeira aba caía em EMPTY.
 * Escolher pelo nome da aba não serve: o usuário renomeia e o nome do
 * modelo varia por idioma.
 */
export function headerHasRequiredColumns(header: CellValue[], maps: LabelMaps): boolean {
  const cols = resolveHeaderColumns(header, maps);
  return REQUIRED_COLUMNS.every((key) => cols.has(key));
}

/**
 * Valida a planilha inteira e monta o plano de importação.
 *
 * - `cells`: matriz crua vinda do `excelIO.readSheetRows` (linha 0 =
 *   header). Linhas 100% vazias são ignoradas (não contam no limite nem
 *   no relatório).
 * - `existingNamesNormalized`: nomes do catálogo (ativos + arquivados)
 *   já passados por `normalizeForSearch` — dedupe caixa+acento-insensível.
 * - `existingCount`: total de produtos do tenant (ativos + arquivados) —
 *   linhas válidas além de `MAX_PRODUCTS_PER_TENANT - existingCount`
 *   viram `LIMIT_REACHED` (importa até completar o teto).
 * - Duplicata dentro da planilha: a primeira ocorrência VÁLIDA ganha;
 *   as seguintes viram `DUPLICATE_SHEET`. Linha com erro não consome o
 *   nome (uma ocorrência válida depois ainda pode importar).
 */
export function parseSheet(
  cells: CellValue[][],
  maps: LabelMaps,
  existingNamesNormalized: ReadonlySet<string>,
  existingCount: number,
): ParseSheetResult {
  if (cells.every((row) => isRowEmpty(row))) {
    return { kind: 'SHEET_ERROR', error: 'EMPTY', missingColumns: [] };
  }

  const cols = resolveHeaderColumns(cells[0] ?? [], maps);

  const missing = REQUIRED_COLUMNS.filter((key) => !cols.has(key));
  if (missing.length > 0) {
    return { kind: 'SHEET_ERROR', error: 'MISSING_COLUMNS', missingColumns: missing };
  }

  const dataRows: { rowNumber: number; row: CellValue[] }[] = [];
  for (let i = 1; i < cells.length; i++) {
    const row = cells[i] ?? [];
    if (isRowEmpty(row)) continue;
    dataRows.push({ rowNumber: i + 1, row });
  }
  if (dataRows.length === 0) {
    return { kind: 'SHEET_ERROR', error: 'EMPTY', missingColumns: [] };
  }
  if (dataRows.length > IMPORT_MAX_ROWS) {
    return { kind: 'SHEET_ERROR', error: 'TOO_MANY_ROWS', missingColumns: [] };
  }

  const seenNames = new Set<string>();
  const rows: ReviewRow[] = [];
  for (const { rowNumber, row } of dataRows) {
    const parsed = parseRow(row, rowNumber, cols, maps);
    if (parsed.status !== 'READY') {
      rows.push(parsed);
      continue;
    }
    const normName = normalizeForSearch(parsed.name);
    if (existingNamesNormalized.has(normName)) {
      rows.push({ ...parsed, status: 'DUPLICATE_CATALOG', values: null });
    } else if (seenNames.has(normName)) {
      rows.push({ ...parsed, status: 'DUPLICATE_SHEET', values: null });
    } else {
      seenNames.add(normName);
      rows.push(parsed);
    }
  }

  const capped = applyCapacity(rows, existingCount);
  return { kind: 'OK', plan: { rows: capped, counts: countRows(capped) } };
}

function countRows(rows: ReviewRow[]): ImportPlan['counts'] {
  return {
    ready: rows.filter((r) => r.status === 'READY').length,
    duplicates: rows.filter(
      (r) => r.status === 'DUPLICATE_CATALOG' || r.status === 'DUPLICATE_SHEET',
    ).length,
    errors: rows.filter((r) => r.status === 'ERROR').length,
    limited: rows.filter((r) => r.status === 'LIMIT_REACHED').length,
  };
}

/**
 * Re-checa o plano contra o catálogo ATUAL imediatamente antes de gravar.
 * O dedupe e o teto do parse congelam o snapshot da hora do upload; se um
 * colega criou produtos enquanto a revisão estava aberta, a linha READY
 * colidente vira `DUPLICATE_CATALOG` e o teto é re-aplicado com a
 * contagem fresca — a promessa "nunca duplica / nunca passa de 1.000"
 * não pode depender de snapshot velho. Desde a #776 o servidor
 * (`importProducts`) aplica a mesma regra ao gravar; isto é o espelho que
 * deixa o cliente mostrar o resultado antes de enviar.
 *
 * Demote-only: só linhas que a revisão mostrou como READY podem gravar;
 * `LIMIT_REACHED` não é promovida mesmo que o dedupe libere vaga — o
 * usuário aprovou um conjunto específico e o confirm nunca grava além
 * dele.
 */
export function reconcilePlanAgainstCatalog(
  plan: ImportPlan,
  existingNamesNormalized: ReadonlySet<string>,
  existingCount: number,
): ImportPlan {
  const deduped = plan.rows.map((row): ReviewRow => {
    if (row.status !== 'READY') return row;
    if (!existingNamesNormalized.has(normalizeForSearch(row.name))) return row;
    return { ...row, status: 'DUPLICATE_CATALOG', values: null };
  });
  const rows = applyCapacity(deduped, existingCount);
  return { rows, counts: countRows(rows) };
}

/** Relatório da gravação — o que foi criado e o que ficou de fora, e por quê. */
export type ImportReport = {
  imported: number;
  failed: ReviewRow[];
  /** Linhas que nem gravaram: duplicadas + com erro (revisão ou servidor). */
  skipped: number;
  /** Linhas válidas puladas pelo teto de MAX_PRODUCTS_PER_TENANT. */
  limited: number;
};

/** Linha como a callable `importProducts` a recebe (`ImportProductRowSchema`). */
export type ImportWireRow = {
  rowNumber: number;
  fields: Omit<ImportProductValues, 'minStock'>;
  minStock: number | null;
};
/** Status por linha devolvido pela callable (`ImportRowResultStatus`). */
export type ImportWireResult = { rowNumber: number; status: string };

/** Linhas por chamada: 100 cabem folgados no payload e no timeout da callable. */
export const IMPORT_CHUNK_SIZE = 100;

/**
 * Grava as linhas `READY` do plano em lotes, pelo `send` injetado (a callable
 * `importProducts` de cada app), e monta o relatório.
 *
 * Origem: #776 — 2026-09-29 13:11 +0200 (era o `runImport` do apps/app, um
 * pool de `createProduct` diretos; virou lotes pela callable e veio para cá
 * para o CRM gravar igual).
 *
 * Contrato: o relatório soma ao que a revisão já pulou o que o SERVIDOR
 * rebaixou: duplicata surgida depois da revisão → `skipped`; teto →
 * `limited`; `INVALID`/`FAILED` → `failed`. Um lote que lança inteiro (rede,
 * permissão) vai todo para `failed`, `onChunkError` é chamado (report de
 * telemetria de cada app) e o próximo lote segue — reenviar a planilha é
 * idempotente (o dedupe pula as já criadas).
 *
 * Usado por: `apps/app` `features/products/import/runImport.ts`; CRM
 * `ProductImportPage`.
 */
export async function runProductImport(
  plan: ImportPlan,
  send: (rows: ImportWireRow[]) => Promise<{ results: ImportWireResult[] }>,
  onProgress: (done: number, total: number) => void,
  onChunkError: (err: unknown, chunk: ReviewRow[]) => void,
): Promise<ImportReport> {
  const readyRows = plan.rows.filter(
    (row): row is ReviewRow & { values: ImportProductValues } => row.status === 'READY' && row.values !== null,
  );
  const byNumber = new Map(readyRows.map((r) => [r.rowNumber, r]));
  const failed: ReviewRow[] = [];
  let imported = 0;
  let skipped = plan.counts.duplicates + plan.counts.errors;
  let limited = plan.counts.limited;
  let done = 0;

  for (let i = 0; i < readyRows.length; i += IMPORT_CHUNK_SIZE) {
    const chunk = readyRows.slice(i, i + IMPORT_CHUNK_SIZE);
    try {
      const out = await send(
        chunk.map((r) => {
          const { minStock, ...fields } = r.values;
          return { rowNumber: r.rowNumber, fields, minStock };
        }),
      );
      for (const res of out.results) {
        const row = byNumber.get(res.rowNumber);
        if (res.status === 'CREATED') imported += 1;
        else if (res.status === 'LIMIT_REACHED') limited += 1;
        else if (res.status === 'DUPLICATE_CATALOG' || res.status === 'DUPLICATE_SHEET') skipped += 1;
        else if (row !== undefined) failed.push(row);
      }
    } catch (err) {
      failed.push(...chunk);
      onChunkError(err, chunk);
    }
    done += chunk.length;
    onProgress(done, readyRows.length);
  }
  return { imported, failed, skipped, limited };
}
